import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import i18next from 'i18next';
import type { ReactNode } from 'react';
import { toast } from 'react-toastify';

import { HostBridge } from '@/lib/hostBridge';
import { ApiRequestError } from '@/types/api.types';
import { appConfig } from '@app/config/appConfig';

interface HttpError {
  statusCode?: number;
  response?: { status?: number };
}

const NO_RETRY_STATUSES = new Set([401, 403, 404, 422]);
const UNAUTHORIZED_TOAST_ID = 'session-expired';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      const err = error as HttpError;
      if (err.statusCode === 401 || err.response?.status === 401) {
        void HostBridge.continue(
          { action: 'exit', reason: 'unauthorized' },
          {
            message: i18next.t(
              'common.sessionExpiredTab',
              'Your session has expired. Please try again.',
            ),
            showToast: (message) => toast.error(message, { toastId: UNAUTHORIZED_TOAST_ID }),
          },
        );
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      const err = error as HttpError;
      if (err.statusCode === 401 || err.response?.status === 401) {
        void HostBridge.continue(
          { action: 'exit', reason: 'unauthorized' },
          {
            message: i18next.t(
              'common.sessionExpiredTab',
              'Your session has expired. Please try again.',
            ),
            showToast: (message) => toast.error(message, { toastId: UNAUTHORIZED_TOAST_ID }),
          },
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && NO_RETRY_STATUSES.has(error.statusCode)) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: appConfig.env === 'production',
    },
    mutations: {
      retry: false,
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Query provider.
 *
 * @param {QueryProviderProps} props - Component props.
 * @param {string | number | bigint | boolean | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | Promise<AwaitedReactNode> | null | undefined} props.children - The children.
 *
 * @returns {JSX.Element} The rendered element.
 */
export function QueryProvider({ children }: Readonly<QueryProviderProps>) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {appConfig.features.devtools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export { queryClient };
